// Types and words for the Disciplinary page (db/migrations/2401,
// docs/DISCIPLINARY-RECOMMENDATIONS.md). Plain .ts so web/test can import it.
import type { ChipTone } from '../components/interviews/Chip'

export type RecStatus = 'filed' | 'accepted' | 'rejected' | 'sending' | 'sent' | 'manager_signed' | 'signed' | 'declined' | 'withdrawn'

export type LetterStatus = 'ready' | 'sending' | 'sent' | 'manager_signed' | 'signed' | 'declined'

/** The warning letter that follows the employee's signature (4800). */
export type WarningLetter = {
  status: LetterStatus | null; version: number; ref: string
  sent_at: string | null; manager_signed_at: string | null; signed_at: string | null
  driver_comment: string | null; declined_role: string | null; send_error: string | null
  issuer: string | null; has_envelope: boolean
}

export type Recommendation = {
  id: string; ref: string; driver_id: string; driver: string | null
  hearing_on: string; chair: string | null; recommended_on: string
  sanction: string; sanction_label: string; charge: string; summary: string | null
  filename: string | null; file_path: string; mime_type: string | null
  filed_by: string | null; filed_at: string
  status: RecStatus; released_by: string | null; released_at: string | null; manager: string | null
  sent_at: string | null; manager_signed_at: string | null; signed_at: string | null
  driver_comment: string | null; declined_role: string | null; send_error: string | null
  reminders_sent: number; last_reminded_at: string | null; last_remind_error: string | null
  last_checked_at: string | null; has_envelope: boolean
  withdrawn_reason: string | null; withdrawn_at: string | null
  on_register: boolean; lapses_on: string | null
  /** The incident case this hearing decides, when it began as a reported incident (4402). */
  case_id: string | null; incident_ref: string | null
  /** What the chair recommended, with the period she set (4800). */
  recommended_months: number | null; recommended_text: string
  /** What Fleetly adopted, by whom and why (4800). */
  decision: 'accepted' | 'rejected' | null; decided_by: string | null; decided_at: string | null; decision_note: string | null
  accepted_sanction: string | null; accepted_months: number | null; accepted_text: string | null
  issued_on: string | null
  can_decide: boolean
  letter: WarningLetter | null
  amendments: { at: string; by: string | null; reason: string; from: string; to: string }[]
}

export type DisciplinaryPage = {
  ok: boolean
  sees_all: boolean
  recommendations: Recommendation[]
  drivers: { id: string; name: string | null; status: string }[]
  chairs: { id: string; name: string | null; is_chair: boolean }[]
  sanctions: { value: string; label: string }[]
  /** Incidents referred to a hearing with no recommendation filed yet (4402). */
  /** The 27.1 periods, only ever a prefill for the chair's own number. */
  default_months: Record<string, number | null>
  hearing_cases: { case_id: string; driver_id: string; ref: string; occurred_on: string; proposed_sanction: string; proposed_label: string; charge: string }[]
}

/** What a recommendation's state reads as, naming who is still to sign. */
export function statusText(r: Pick<Recommendation, 'status' | 'manager' | 'driver' | 'declined_role' | 'send_error'> & { accepted_sanction?: string | null }): { text: string; tone: ChipTone } {
  // 9301: a released dismissal is in effect; the driver's signature is only a
  // receipt, so it is not shown as what the dismissal is waiting for.
  if (r.accepted_sanction === 'termination' && r.status === 'manager_signed') {
    return { text: `Dismissed. Receipt not yet signed by ${r.driver ?? 'the driver'}`, tone: 'red' }
  }
  switch (r.status) {
    case 'filed':
      return { text: 'Filed: accept or reject it', tone: 'amber' }
    case 'accepted':
      return r.send_error ? { text: 'Not sent: SignNow refused it', tone: 'red' } : { text: 'Accepted, not released', tone: 'neutral' }
    case 'rejected':
      return { text: 'Rejected', tone: 'neutral' }
    case 'sending':
      return { text: 'Sending for signature', tone: 'amber' }
    case 'sent':
      return { text: `Waiting for ${r.manager ?? 'the manager'} to sign`, tone: 'amber' }
    case 'manager_signed':
      return { text: `Waiting for ${r.driver ?? 'the driver'} to sign`, tone: 'amber' }
    case 'signed':
      return { text: 'Signed', tone: 'green' }
    case 'declined':
      return r.send_error
        ? { text: 'Not sent again: SignNow refused it', tone: 'red' }
        : { text: `Declined by the ${r.declined_role === 'Manager' ? 'manager' : 'driver'}`, tone: 'red' }
    case 'withdrawn':
      return { text: 'Withdrawn', tone: 'neutral' }
  }
}

export function isWarning(s: string | null | undefined): boolean {
  return s === 'verbal_warning' || s === 'written_warning' || s === 'final_written_warning'
}

/**
 * The months box as the server should read it. Blank is "no figure given", so
 * it goes as null: `Number('')` is 0, and the accept and amend functions took 0
 * as a real period, which would put a warning on the record that lapses the day
 * it is issued (found with Platform Request #33, 6 October 2026). With null,
 * accepting falls back to the chair's period and amending asks for the months.
 */
export function monthsParam(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined) return null
  const s = String(v).trim()
  if (s === '') return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

export function letterText(l: Pick<WarningLetter, 'status' | 'send_error' | 'declined_role'>): { text: string; tone: ChipTone } {
  switch (l.status) {
    case 'ready': return l.send_error ? { text: 'Not sent: SignNow refused it', tone: 'red' } : { text: 'Ready to send', tone: 'amber' }
    case 'sending': return { text: 'Sending for signature', tone: 'amber' }
    case 'sent': return { text: 'Waiting for the issuer to sign', tone: 'amber' }
    case 'manager_signed': return { text: 'Waiting for the employee to sign', tone: 'amber' }
    case 'signed': return { text: 'Signed', tone: 'green' }
    case 'declined': return { text: `Declined by the ${l.declined_role === 'Manager' ? 'issuer' : 'employee'}`, tone: 'red' }
    default: return { text: 'Not ready', tone: 'neutral' }
  }
}

export function sanctionTone(s: string): ChipTone {
  if (s === 'termination') return 'red'
  if (s === 'final_written_warning' || s === 'suspension') return 'amber'
  if (s === 'no_sanction') return 'green'
  return 'neutral'
}

export function day(d: string | null | undefined): string {
  if (!d) return '—'
  return new Date(`${d.slice(0, 10)}T12:00:00+02:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function when(t: string | null | undefined): string {
  if (!t) return '—'
  return new Date(t).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function todaySast(): string {
  return new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 10)
}
